import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPrinter,
  FiClipboard,
  FiSearch,
  FiEdit,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiCheck,
  FiSave,
  FiPlus,
  FiTrash2,
  FiAlertCircle
} from "react-icons/fi";
import { FaBoxes, FaStore } from "react-icons/fa";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const CreateStockOpname = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [stockData, setStockData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionInput, setDescriptionInput] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(
    localStorage.getItem("id_outlet") || ""
  );
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [totalPages, setTotalPages] = useState(1);
  const [pageData, setPageData] = useState([]);

  const [itemObatSelected, setItemObatSelected] = useState({});
  const [batchObatSelected, setBatchObatSelected] = useState([]);
  const [itemAdjust, setItemAdjust] = useState([]);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState({});
  const [needDeleteBatch, setNeedDeleteBatch] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [kartuStockOpname, setKartuStockOpname] = useState([]);
  const [showStockModal, setShowStockModal] = useState(false);

  const fetchObat = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const baseURL = `${process.env.REACT_APP_API_BASE_URL}/v1/drug`;
      const url = `${baseURL}?id_outlet=${selectedOutlet}&page=${page}&per_page=${itemsPerPage}&search=${encodeURIComponent(search)}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const drugs = response.data.data || [];
      setPageData(drugs);

      setStockData((prev) => {
        const updated = [...prev];
        const desiredFormat = "dd-MM-yyyy HH:mm";

        drugs.forEach((drug) => {
          const existingIndex = updated.findIndex((d) => d.id === drug.id);
          if (existingIndex === -1) {
            updated.push({
              id: drug.id,
              name: drug.name,
              dosis: drug.dosis || "",
              stockInSystem: drug.stock,
              stockInWarehouse: "",
              selisih: 0,
              status: "Belum Dihitung",
              note: "",
              batch: drug.batch || [],
              last_updated:
                drug.last_update === "0001-01-01T00:00:00Z" || !drug.last_update
                  ? "N/A"
                  : format(new Date(drug.last_update), desiredFormat),
            });
          } else {
            updated[existingIndex].stockInSystem = drug.stock;
          }
        });
        return updated;
      });

      setTotalPages(response.data.meta?.last_page || 1);
      setCurrentPage(response.data.meta?.current_page || page);
    } catch (error) {
      console.error("Gagal mengambil data obat:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOutlet) {
      fetchObat(currentPage, searchTerm);
    }
  }, [selectedOutlet, currentPage, searchTerm]);

  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/outlet`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const outletList = response.data.data || [];
        setOutlets(outletList);
      } catch (error) {
        console.error("Gagal mengambil data outlet:", error);
      }
    };

    const userRole = (localStorage.getItem("role") || "").toLowerCase();
    setRole(userRole);
    if (["owner", "admin", "superadmin"].includes(userRole)) {
      fetchOutlets();
    }
  }, [token]);

  const openDescModal = () => {
    if (itemAdjust.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Peringatan",
        text: "Belum ada obat yang di-adjust/disesuaikan!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }
    setShowDescriptionModal(true);
  };

  const handleSubmit = async () => {
    setLoadingSubmit(true);
    try {
      const outlet_id = parseInt(
        selectedOutlet || localStorage.getItem("id_outlet"),
        10
      );
      const employee_id = parseInt(localStorage.getItem("employee_id"), 10) || 1;

      const items = itemAdjust.map((item) => ({
        obat_id: item.obat_id,
        stock_system: parseInt(item.stock_system, 10),
        batch: item.batch.map((b) => ({
          ...b,
          expired_at: b.expired_at?.split("T")[0],
        })),
        need_delete: item.need_delete || [],
        note: item.note || "",
      }));

      const payload = {
        outlet_id,
        employee_id,
        items,
        description: descriptionInput,
      };

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v2/stock-opname`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setShowDescriptionModal(false);
      setDescriptionInput("");

      await Swal.fire({
        title: "Berhasil Diproses!",
        text: "Stock Opname berhasil disimpan dan stok telah diperbarui.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      navigate("/inventory/stock-opname");
    } catch (err) {
      console.error("Gagal submit stock opname:", err);
      Swal.fire({
        title: "Gagal Memproses",
        text: err.response?.data?.message || "Terjadi kesalahan saat memproses Stock Opname.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const tableRows = pageData
      .map((item, index) => {
        return `
          <tr>
            <td style="border:1px solid #ccc; padding:8px; text-align:center;">${index + 1}</td>
            <td style="border:1px solid #ccc; padding:8px;">${item.name} ${item.dosis || ""}</td>
            <td style="border:1px solid #ccc; padding:8px; text-align:center;">${item.stock}</td>
            <td style="border:1px solid #ccc; padding:8px; text-align:center;">......</td>
          </tr>
        `;
      })
      .join("");

    const html = `
      <html>
        <head>
          <title>Kartu Hitung Stock Opname</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { margin-bottom: 5px; }
            table { border-collapse: collapse; width: 100%; margin-top: 15px; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Apotek Djaya Medika - Kartu Stock Opname</h2>
          <p style="font-size:12px; color:#666;">Tanggal: ${new Date().toLocaleDateString("id-ID")}</p>
          <table>
            <thead>
              <tr>
                <th style="border:1px solid #ccc; padding:8px;">No</th>
                <th style="border:1px solid #ccc; padding:8px;">Nama Obat</th>
                <th style="border:1px solid #ccc; padding:8px;">Stok Sistem</th>
                <th style="border:1px solid #ccc; padding:8px;">Stok Fisik Gudang</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const openModalAturStock = (item) => {
    setItemObatSelected(item);
    const adjustedItem = itemAdjust.find((i) => i.obat_id === item.id);
    setBatchObatSelected(
      adjustedItem ? [...adjustedItem.batch] : [...(item.batch || [])]
    );
    setNote(notes[item.id] || adjustedItem?.note || "");
    setShowStockModal(true);
  };

  const closeModal = () => {
    setNeedDeleteBatch([]);
    setShowStockModal(false);
  };

  const handleBatchChange = (index, field, value) => {
    setBatchObatSelected((prev) => {
      const updated = [...prev];
      let newValue = value;

      if (field === "stock") {
        if (prev[index][field] === 0 && value !== "") {
          newValue = value.replace(/^0+/, "");
        }
        if (!/^\d*$/.test(newValue)) {
          return prev;
        }
        newValue = newValue === "" ? 0 : parseInt(newValue, 10);
      }
      updated[index] = { ...updated[index], [field]: newValue };
      return updated;
    });
  };

  const handleAddToNeedDelete = (batch) => {
    const activeBatches = batchObatSelected.filter(
      (b) =>
        !itemAdjust
          .find((i) => i.obat_id === itemObatSelected.id)
          ?.need_delete?.includes(b.id)
    );

    if (activeBatches.length <= 1) {
      Swal.fire({
        icon: "warning",
        title: "Peringatan",
        text: "Minimal harus ada 1 batch obat aktif.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    Swal.fire({
      title: `Hapus Batch Expired ${batch.expired_at?.split("T")[0]}?`,
      text: `Stok batch ini: ${batch.stock}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    }).then((result) => {
      if (result.isConfirmed) {
        setBatchObatSelected((prev) => prev.filter((b) => b.id !== batch.id));
        setNeedDeleteBatch((prev) => [...prev, batch.id]);
      }
    });
  };

  const handleAdjustItem = (item, note, batchChange) => {
    setItemAdjust((prev) => {
      const existingIndex = prev.findIndex((i) => i.obat_id === item.id);
      const isOriginalBatch =
        JSON.stringify(item.batch) === JSON.stringify(batchChange) &&
        !note.trim();

      if (isOriginalBatch) {
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated.splice(existingIndex, 1);
          return updated;
        }
        return prev;
      }

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          obat_id: item.id,
          stock_system: item.stock,
          note: note,
          batch: batchChange,
          need_delete: (updated[existingIndex].need_delete || []).concat(needDeleteBatch),
        };
        return updated;
      }

      return [
        ...prev,
        {
          obat_id: item.id,
          stock_system: item.stock,
          note: note,
          batch: batchChange,
          need_delete: needDeleteBatch,
        },
      ];
    });

    setNeedDeleteBatch([]);
    closeModal();
  };

  const getDiff = (drug) => {
    const found = itemAdjust.find((itm) => itm.obat_id === drug.id);
    const total = found
      ? found.batch?.reduce((sum, b) => sum + Number(b.stock || 0), 0)
      : drug.batch?.reduce((sum, b) => sum + Number(b.stock || 0), 0);

    return (total || 0) - (drug.stock || 0);
  };

  const getStatus = (drug) => {
    const adjustedItem = itemAdjust.find((itm) => itm.obat_id === drug.id);
    if (!adjustedItem) return "Belum Dihitung";

    const totalBatchStock = adjustedItem.batch?.reduce(
      (sum, b) => sum + Number(b.stock || 0),
      0
    );
    return totalBatchStock === drug.stock ? "Disesuaikan (Sama)" : "Disesuaikan";
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/stock-opname")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Proses Stock Opname
            </h1>
            <p className="text-xs text-gray-500">
              Sesuaikan stok batch & fisik gudang dengan stok sistem
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
          >
            <FiPrinter className="w-4 h-4 text-emerald-600" />
            <span>Cetak Kartu Opname</span>
          </button>

          {/* Rebalance Submit CTA (GREEN BUTTON) */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openDescModal}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-white text-xs font-bold shadow-lg transition-all ${
              itemAdjust.length === 0
                ? "bg-gray-300 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-600/20"
            }`}
          >
            <FiClipboard className="w-4 h-4" />
            <span>Simpan Opname ({itemAdjust.length})</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Main Table Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
      >
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama obat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex items-center gap-4 w-full sm:w-auto justify-end text-xs">
            {["owner", "admin", "superadmin"].includes(role) && (
              <select
                value={selectedOutlet}
                onChange={(e) => {
                  setSelectedOutlet(e.target.value);
                  localStorage.setItem("id_outlet", e.target.value);
                }}
                className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="">-- Pilih Outlet --</option>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-semibold">Tampilkan:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 font-semibold focus:outline-none"
              >
                <option value={10}>10 Baris</option>
                <option value={25}>25 Baris</option>
                <option value={50}>50 Baris</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Obat & Dosis</th>
                  <th className="p-3.5 text-center">Stok Sistem</th>
                  <th className="p-3.5 text-center">Selisih Stok</th>
                  <th className="p-3.5 text-center">Status Opname</th>
                  <th className="p-3.5 text-center">Aksi Adjust</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageData.map((drug, index) => {
                  const diff = getDiff(drug);
                  const status = getStatus(drug);

                  return (
                    <tr key={drug.id} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 text-center font-semibold text-gray-400">
                        {index + 1 + (currentPage - 1) * itemsPerPage}
                      </td>
                      <td className="p-3.5 font-bold text-gray-900">
                        {drug.name} <span className="text-gray-500 text-[11px]">{drug.dosis}</span>
                      </td>
                      <td className="p-3.5 text-center font-mono font-bold text-gray-800">
                        {drug.stock}
                      </td>
                      <td className="p-3.5 text-center font-bold">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-mono font-bold ${
                            diff === 0
                              ? "bg-gray-100 text-gray-600"
                              : diff > 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-rose-100 text-rose-700"
                          }`}
                        >
                          {diff > 0 ? `+${diff}` : diff}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold ${
                            status.includes("Disesuaikan")
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <button
                          onClick={() => openModalAturStock(drug)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-xs transition-all hover:scale-105 border border-emerald-200"
                        >
                          <FiEdit className="w-3.5 h-3.5" />
                          <span>Adjust Batch</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <span className="text-gray-400">
            Halaman <span className="font-bold text-gray-700">{currentPage}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalPages}</span>
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* ADJUST BATCH MODAL */}
      <AnimatePresence>
        {showStockModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Atur Batch & Stok Fisik
                  </h3>
                  <p className="text-xs text-emerald-700 font-semibold">
                    {itemObatSelected.name} {itemObatSelected.dosis}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5 overflow-y-auto max-h-[60vh] text-xs">
                <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                        <th className="p-3 w-10 text-center">No</th>
                        <th className="p-3">Tanggal Expired</th>
                        <th className="p-3">Stok Batch</th>
                        <th className="p-3 text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {batchObatSelected
                        ?.filter(
                          (batch) =>
                            !itemAdjust
                              .find((i) => i.obat_id === itemObatSelected.id)
                              ?.need_delete?.includes(batch.id)
                        )
                        .map((batch, index) => (
                          <tr key={batch.id || index}>
                            <td className="p-3 text-center font-bold text-gray-400">
                              {index + 1}
                            </td>
                            <td className="p-3">
                              <input
                                type="date"
                                value={batch.expired_at?.split("T")[0] || ""}
                                onChange={(e) =>
                                  handleBatchChange(index, "expired_at", e.target.value)
                                }
                                className="p-2 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-emerald-500"
                              />
                            </td>
                            <td className="p-3">
                              <input
                                type="text"
                                value={batch.stock}
                                onChange={(e) =>
                                  handleBatchChange(index, "stock", e.target.value || "0")
                                }
                                className="p-2 border border-gray-200 rounded-xl text-xs font-mono font-bold focus:outline-none focus:border-emerald-500 w-28"
                              />
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => handleAddToNeedDelete(batch)}
                                className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                                title="Hapus Batch"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Catatan Khusus Obat Ini
                  </label>
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value);
                      setNotes((prev) => ({
                        ...prev,
                        [itemObatSelected.id]: e.target.value,
                      }));
                    }}
                    placeholder="Tulis alasan perbedaan stok atau kondisi fisik obat..."
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
                <button
                  onClick={closeModal}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold transition-colors"
                >
                  Batal
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    handleAdjustItem(
                      itemObatSelected,
                      note,
                      batchObatSelected?.filter(
                        (batch) =>
                          !itemAdjust
                            .find((i) => i.obat_id === itemObatSelected.id)
                            ?.need_delete?.includes(batch.id)
                      )
                    )
                  }
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                >
                  <FiCheck className="w-4 h-4" />
                  <span>Simpan Penyesuaian</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DESKRIPSI UTAMA STOCK OPNAME */}
      <AnimatePresence>
        {showDescriptionModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Konfirmasi Stock Opname
                  </h3>
                  <p className="text-xs text-gray-500">
                    Tulis deskripsi / berita acara opname sebelum menyimpan
                  </p>
                </div>
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-800 font-semibold">
                  Total {itemAdjust.length} item obat telah disesuaikan stoknya.
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Deskripsi / Catatan Opname
                  </label>
                  <textarea
                    rows={4}
                    value={descriptionInput}
                    onChange={(e) => setDescriptionInput(e.target.value)}
                    placeholder="Contoh: Opname rutin bulanan obat bebas & resep..."
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold transition-colors"
                >
                  Batal
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={loadingSubmit}
                  className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
                >
                  <FiSave className="w-4 h-4" />
                  <span>{loadingSubmit ? "Memproses..." : "Submit Opname"}</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateStockOpname;
